import { analyticsBlocked } from '../../utils/analyticsPrivacy';
import { useEffect } from 'react';
import { COOKIE_CONSENT_CHANGED_EVENT, getStoredConsent } from '../../hooks/useCookieConsent';

const MAILCHIMP_SCRIPT_ID = 'mcjs';
const MAILCHIMP_SRC =
  'https://chimpstatic.com/mcjs-connected/js/users/4cbb815e28abc7378e730c0bd/62c36c166b0db1642ce629de8.js';

// requestIdleCallback / cancelIdleCallback are already declared on Window by
// the TS DOM lib; no local re-declaration needed.
type IdleHandle = number;

const hasMarketingConsent = (): boolean => {
  const storedConsent = getStoredConsent();
  return !analyticsBlocked() && Boolean(storedConsent?.preferences.marketing);
};

const injectMailchimpScript = () => {
  if (typeof document === 'undefined' || !hasMarketingConsent() || document.getElementById(MAILCHIMP_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = MAILCHIMP_SCRIPT_ID;
  script.async = true;
  script.src = MAILCHIMP_SRC;
  document.head.appendChild(script);
};

const MailchimpTracking = () => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let idleHandle: IdleHandle | null = null;
    let usesIdleCallback = false;
    let generation = 0;

    const cancelPendingLoad = () => {
      generation += 1;
      if (idleHandle === null) return;
      if (usesIdleCallback) window.cancelIdleCallback(idleHandle);
      else window.clearTimeout(idleHandle);
      idleHandle = null;
    };

    const scheduleLoad = () => {
      if (!hasMarketingConsent()) {
        cancelPendingLoad();
        return;
      }
      if (idleHandle !== null || document.getElementById(MAILCHIMP_SCRIPT_ID)) return;

      const scheduledGeneration = generation;
      const load = () => {
        // Also guard callbacks already dispatched when consent changed or we unmounted.
        if (scheduledGeneration !== generation) return;
        idleHandle = null;
        injectMailchimpScript();
      };
      usesIdleCallback = typeof window.requestIdleCallback === 'function' &&
        typeof window.cancelIdleCallback === 'function';
      idleHandle = usesIdleCallback
        ? window.requestIdleCallback(load, { timeout: 2500 })
        : window.setTimeout(load, 1500);
    };

    const handleConsentChange = () => scheduleLoad();

    scheduleLoad();

    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, handleConsentChange);
    window.addEventListener('storage', handleConsentChange);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, handleConsentChange);
      window.removeEventListener('storage', handleConsentChange);

      cancelPendingLoad();
    };
  }, []);

  return null;
};

export default MailchimpTracking;

